import {
  Component, DestroyRef, Inject, computed, inject, signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CandidateProject,
  CandidateProjectsPage,
  SupportService
} from '../../../core/services/support.service';
import { supportErrorKey } from '../../../core/services/support-errors';

export interface ProjectPickerDialogData {
  orderItemId: number;
  /** Display name of the currently-linked project, shown for context. */
  currentProjectName?: string;
}

const SEARCH_DEBOUNCE_MS = 250;
const PAGE_SIZE = 50;

/**
 * Picker for the "Substituir" action. Lists same-customer + same-product
 * projects from `/support/order-items/:id/candidate-projects`, lets the
 * agent search by name, click to select, then confirm. Returns the chosen
 * project id (number) on close, or `null` on cancel.
 *
 * The backend already enforces customer + product match server-side, so
 * any mistake here surfaces as PROJECT_CUSTOMER_MISMATCH /
 * PROJECT_PRODUCT_MISMATCH on the subsequent swap call — which the menu
 * translates via supportErrorKey().
 *
 * Pagination: the endpoint returns a `{ items, pagination }` envelope.
 * Page 1 loads on open / on every query change (replaces the list).
 * Subsequent pages append via `loadMore()`. The footer surfaces
 * "X–Y of N" so the agent can decide whether to refine the search.
 */
@Component({
  selector: 'app-project-picker-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    TranslateModule
  ],
  templateUrl: './project-picker-dialog.component.html',
  styleUrl: './project-picker-dialog.component.scss'
})
export class ProjectPickerDialogComponent {
  private readonly supportService = inject(SupportService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(MatDialogRef<ProjectPickerDialogComponent, number | null>);

  readonly query = signal('');
  readonly loading = signal(true);
  readonly loadingMore = signal(false);
  readonly error = signal<string | null>(null);
  readonly candidates = signal<CandidateProject[]>([]);
  readonly selectedId = signal<number | null>(null);

  /** Last received pagination block. Drives the footer + load-more gating. */
  readonly pagination = signal<CandidateProjectsPage['pagination'] | null>(null);

  readonly hasResults = computed(() => this.candidates().length > 0);
  readonly isEmptySearch = computed(() => this.query().trim().length === 0);
  readonly hasMore = computed(() => {
    const p = this.pagination();
    return !!p && p.current_page < p.total_pages;
  });

  private readonly query$ = new Subject<string>();

  constructor(@Inject(MAT_DIALOG_DATA) public readonly data: ProjectPickerDialogData) {
    // Initial load (no query, page 1).
    this.fetchFirstPage('');

    // Debounced re-search on input. switchMap cancels stale in-flight
    // requests so the visible list always matches the current query.
    // Always lands on page 1 — switching pages mid-typing would be racy.
    this.query$
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap(q => {
          this.loading.set(true);
          this.error.set(null);
          return this.supportService.listCandidateProjects(
            this.data.orderItemId,
            q.trim(),
            PAGE_SIZE,
            1
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: page => this.applyFirstPage(page),
        error: err => this.handleError(err, 'search')
      });
  }

  onQueryChange(value: string): void {
    this.query.set(value);
    this.selectedId.set(null);
    this.query$.next(value);
  }

  selectProject(project: CandidateProject): void {
    this.selectedId.set(project.id);
  }

  confirm(): void {
    const id = this.selectedId();
    if (id == null) return;
    this.dialogRef.close(id);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  displayName(project: CandidateProject): string {
    return project.originalName || project.name || `#${project.id}`;
  }

  /**
   * Append the next page to the existing list. No-op if already loading,
   * already on the last page, or no pagination state yet. Selection is
   * preserved — the user shouldn't lose their pick when fetching more.
   */
  loadMore(): void {
    if (this.loadingMore() || this.loading()) return;
    const p = this.pagination();
    if (!p || p.current_page >= p.total_pages) return;

    const nextPage = p.current_page + 1;
    this.loadingMore.set(true);
    this.error.set(null);

    this.supportService
      .listCandidateProjects(this.data.orderItemId, this.query().trim(), PAGE_SIZE, nextPage)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: page => {
          this.candidates.update(curr => [...curr, ...(page.items ?? [])]);
          this.pagination.set(page.pagination);
          this.loadingMore.set(false);
        },
        error: err => {
          this.loadingMore.set(false);
          // Don't blow away the existing list on a load-more failure —
          // surface the message and let the agent retry.
          this.error.set(this.translate.instant(supportErrorKey(err)));
          console.error('[ProjectPickerDialog] loadMore failed', err);
        }
      });
  }

  private fetchFirstPage(q: string): void {
    this.loading.set(true);
    this.error.set(null);
    this.supportService
      .listCandidateProjects(this.data.orderItemId, q.trim(), PAGE_SIZE, 1)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: page => this.applyFirstPage(page),
        error: err => this.handleError(err, 'initial load')
      });
  }

  private applyFirstPage(page: CandidateProjectsPage): void {
    this.candidates.set(page.items ?? []);
    this.pagination.set(page.pagination);
    this.loading.set(false);
  }

  private handleError(err: unknown, label: string): void {
    console.error(`[ProjectPickerDialog] ${label} failed`, err);
    this.candidates.set([]);
    this.pagination.set(null);
    this.error.set(this.translate.instant(supportErrorKey(err)));
    this.loading.set(false);
  }
}
